import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  createMaintenanceType,
  listMaintenanceTypes,
  updateMaintenanceType,
  type MaintenanceType,
} from "@/lib/wartungen";

interface CategoryRow {
  key: string;
  singular: string;
  plural: string;
}

function coerce(item: MaintenanceType): CategoryRow {
  const key = String(item.key || "").trim();
  const plural = String(item.name_plural || item.name || key || "Unbenannte Kategorie").trim();
  const singular = String(item.name_singular || plural).trim();
  return { key, plural, singular };
}

export function WartungenEinstellungenPage() {
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newKey, setNewKey] = useState("");
  const [newSingular, setNewSingular] = useState("");
  const [newPlural, setNewPlural] = useState("");
  const [createFeedback, setCreateFeedback] = useState<{ tone: "info" | "success" | "error"; msg: string } | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await listMaintenanceTypes();
      setRows(items.map(coerce).filter((r) => r.key));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Wartungskategorien konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const key = newKey.trim().toLowerCase();
    const singular = newSingular.trim();
    const plural = newPlural.trim();
    if (!key || !singular || !plural) {
      setCreateFeedback({ tone: "error", msg: "Bitte Key, Singular und Plural ausfüllen." });
      return;
    }
    setCreating(true);
    setCreateFeedback({ tone: "info", msg: "Kategorie wird angelegt …" });
    try {
      await createMaintenanceType({ key, name_singular: singular, name_plural: plural });
      setNewKey("");
      setNewSingular("");
      setNewPlural("");
      setCreateFeedback({ tone: "success", msg: "Kategorie wurde erfolgreich angelegt." });
      await reload();
    } catch (err) {
      setCreateFeedback({
        tone: "error",
        msg: err instanceof ApiError ? err.message : "Kategorie konnte nicht angelegt werden.",
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    <AppShell
      title="Wartungskategorien"
      subtitle="Kategorien anlegen und Bezeichnungen pflegen. Formulare werden je Kategorie verwaltet."
      back={{ to: "/wartungen", label: "zurück zur Übersicht" }}
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h2 className="text-base font-semibold text-label">Neue Kategorie anlegen</h2>
        <form onSubmit={handleCreate} className="mt-3 grid gap-3 sm:grid-cols-3">
          <TextInput
            label="Key (Schlüssel)"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="z. B. brandschutztueren"
            autoComplete="off"
          />
          <TextInput
            label="Bezeichnung Singular"
            value={newSingular}
            onChange={(e) => setNewSingular(e.target.value)}
            placeholder="z. B. Brandschutztür"
            autoComplete="off"
          />
          <TextInput
            label="Bezeichnung Plural"
            value={newPlural}
            onChange={(e) => setNewPlural(e.target.value)}
            placeholder="z. B. Brandschutztüren"
            autoComplete="off"
          />
          <div className="sm:col-span-3 flex items-center gap-3">
            <Button type="submit" loading={creating}>
              Kategorie anlegen
            </Button>
            {createFeedback ? (
              <span
                className={
                  createFeedback.tone === "error"
                    ? "text-sm text-status-red"
                    : createFeedback.tone === "success"
                      ? "text-sm text-status-green"
                      : "text-sm text-label-secondary"
                }
              >
                {createFeedback.msg}
              </span>
            ) : null}
          </div>
        </form>
      </section>

      <section className="mt-6">
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Lade Kategorien …
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine Wartungskategorien verfügbar.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((row) => (
              <CategoryCard key={row.key} row={row} onSaved={reload} />
            ))}
          </div>
        )}
      </section>
    </AppShell>
  );
}

function CategoryCard({ row, onSaved }: { row: CategoryRow; onSaved: () => Promise<void> }) {
  const [singular, setSingular] = useState(row.singular);
  const [plural, setPlural] = useState(row.plural);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "ok" | "err" | "muted"; msg: string } | null>(null);

  useEffect(() => {
    setSingular(row.singular);
    setPlural(row.plural);
  }, [row.singular, row.plural]);

  async function save() {
    if (!singular.trim() || !plural.trim()) {
      setFeedback({ tone: "err", msg: "Singular und Plural dürfen nicht leer sein." });
      return;
    }
    setSaving(true);
    setFeedback({ tone: "muted", msg: "Speichern …" });
    try {
      await updateMaintenanceType(row.key, {
        name_singular: singular.trim(),
        name_plural: plural.trim(),
      });
      setFeedback({ tone: "ok", msg: "Gespeichert." });
      await onSaved();
    } catch (err) {
      setFeedback({ tone: "err", msg: err instanceof ApiError ? err.message : "Speichern fehlgeschlagen." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="flex flex-col rounded-ios border border-separator bg-card p-4 shadow-ios">
      <h3 className="text-base font-semibold text-label">{row.plural}</h3>
      <p className="mt-0.5 text-xs text-label-secondary">Singular: {row.singular}</p>
      <p className="mb-3 text-xs text-label-tertiary">Typ: {row.key}</p>
      <div className="flex flex-wrap gap-2">
        <Link
          to={`/wartungen/formulare?kategorie=${encodeURIComponent(row.key)}`}
          className="inline-flex h-8 items-center rounded-lg bg-accent px-3 text-sm font-medium text-label-on-accent hover:bg-accent-hover"
        >
          Formulare verwalten
        </Link>
        <Link
          to={`/wartungen/kategorie?kategorie=${encodeURIComponent(row.key)}`}
          className="inline-flex h-8 items-center rounded-lg bg-card px-3 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
        >
          Kategorie-Seite
        </Link>
      </div>

      <button
        type="button"
        className="mt-3 self-start text-sm font-medium text-accent hover:underline"
        onClick={() => setEditing((v) => !v)}
      >
        {editing ? "Bearbeitung schließen" : "Kategorie bearbeiten"}
      </button>

      {editing ? (
        <div className="mt-3 space-y-2 border-t border-separator pt-3">
          <TextInput
            label="Bezeichnung Singular"
            value={singular}
            onChange={(e) => setSingular(e.target.value)}
            autoComplete="off"
          />
          <TextInput
            label="Bezeichnung Plural"
            value={plural}
            onChange={(e) => setPlural(e.target.value)}
            autoComplete="off"
          />
          <div className="flex items-center gap-3">
            <Button type="button" size="sm" loading={saving} onClick={() => void save()}>
              Speichern
            </Button>
            {feedback ? (
              <span
                className={
                  feedback.tone === "err"
                    ? "text-xs text-status-red"
                    : feedback.tone === "ok"
                      ? "text-xs text-status-green"
                      : "text-xs text-label-secondary"
                }
              >
                {feedback.msg}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
